import React, { useState } from "react";
import {
  AutocompleteInput,
  Datagrid,
  Filter,
  FunctionField,
  PaginationLimit,
  ReferenceField,
  ReferenceInput,
  ResourceContextProvider,
  SearchInput,
  TextField,
  useDataProvider,
  useNotify,
  useRedirect,
} from "react-admin";
import { availableOnlySuperAdmin } from "utils/validation";
import CustomPriceField from "../../../components/CustomPriceField";
import StatusField from "../../../components/StatusField";
import BulkConfirmButton from "./BulkConfirmButton";
import EmptyComponent from "./EmptyComponent";
import ApproveButton from "./ApproveButton";
import RejectButton from "./RejectButton";
import UpdatedByField from "../../../components/UpdatedByField";
import SubcontractorAdjustmentPricePanel from "./SubcontractorAdjustmentPricePanel";
import LogButton from "./LogButton";
import DeleteButton from "./DeleteButton";
import CustomPagination from "components/CustomPagination";
import { StyledButton } from "../../../components/ListShowButton";
import { PaddingDenseStyled2List } from "../../../components/ListWrapper";
import ActionsMenu from "../../../components/ActionsMenu";

interface IAdjustmentPriceTable {
  subcontractorId?: number;
  isFixedHeight?: boolean;
  status?: "Draft" | "Approved" | "Rejected";
  isShowTitle?: boolean;
}

const SubcontractorAdjustmentPriceFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />

    {props.isNotFilteredBySubcontractor && (
      <ReferenceInput
        reference="subcontractors"
        source="SubcontractorId"
        filterToQuery={(searchText) => {
          if (searchText !== "") return { search: searchText };
        }}
        sort={{ field: "name", order: "ASC" }}
        alwaysOn
      >
        <AutocompleteInput optionText="name" />
      </ReferenceInput>
    )}

    <ReferenceInput
      source="SchoolId"
      reference="schools"
      filterToQuery={(searchText) => {
        if (searchText !== "") return { search: searchText };
      }}
      sort={{ field: "name", order: "ASC" }}
      alwaysOn
    >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

/**
 * If the current adjustment price record is in "approved" status,
 * we need to check if there is already an existing adjustment price
 * in "Draft" or "Rejected" status that matches the same subcontractor, school, wave, and route.
 * If such a record exists, we will edit that record.
 * Otherwise, we will edit the current record.
 */
const SubcontractorAdjustmentPriceEditButton = (
  record: ISubcontractorAdjustmentPrice,
) => {
  const [loading, setLoading] = useState<boolean>(false);

  const provider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();

  const handleEditAdjustmentPrice = async () => {
    if (record.status === "approved") {
      setLoading(true);
      try {
        // get record with status Draft or Rejected
        const subcontractorAdjustmentPrices = await provider.getList(
          "subcontractorAdjustmentPrices",
          {
            filter: {
              SubcontractorId: record.subcontractorId,
              SchoolId: record.schoolId,
              BusSchedulePlanId: record.busSchedulePlanId,
              RouteId: record.routeId,
              StatusIn: ["Draft", "Rejected"],
            },
            pagination: {
              page: 1,
              perPage: 10,
            },
            sort: {
              field: "Id",
              order: "DESC",
            },
          },
        );

        setLoading(false);

        if (subcontractorAdjustmentPrices.data.length > 0) {
          const subcontractorAdjustmentPrice =
            subcontractorAdjustmentPrices.data[0];
          redirect(
            `/subcontractorAdjustmentPrices/${subcontractorAdjustmentPrice.id}?subcontractorId=${record.subcontractorId}`,
          );
        } else {
          redirect(
            `/subcontractorAdjustmentPrices/${record.id}?subcontractorId=${record.subcontractorId}`,
          );
        }
      } catch (error: any) {
        setLoading(false);
        notify(
          `Failed get existing adjustment price status, error: ${error.message}`,
          "warning",
        );
      }
    } else {
      redirect(
        `/subcontractorAdjustmentPrices/${record.id}?subcontractorId=${record.subcontractorId}`,
      );
    }
  };

  return (
    <StyledButton
      variant="outlined"
      onClick={handleEditAdjustmentPrice}
      style={{ color: "#ffbf00", borderColor: "#ffbf00" }}
      disabled={loading}
    >
      Edit
    </StyledButton>
  );
};

const SubcontractorAdjustmentPriceTable = ({
  subcontractorId,
  isFixedHeight,
  status,
  isShowTitle,
}: IAdjustmentPriceTable) => {
  const isNotFilteredBySubcontractor = !subcontractorId;
  const isPending = status === "Draft";

  return (
    <ResourceContextProvider value="subcontractorAdjustmentPrices">
      <PaddingDenseStyled2List
        basePath="/subcontractorAdjustmentPrices"
        actions={null}
        exporter={false}
        hasCreate={false}
        filters={
          <SubcontractorAdjustmentPriceFilter
            isNotFilteredBySubcontractor={isNotFilteredBySubcontractor}
          />
        }
        filterDefaultValues={{
          SubcontractorId: subcontractorId,
          Status: status,
        }}
        sort={{ field: "id", order: "DESC" }}
        bulkActionButtons={
          isPending && availableOnlySuperAdmin() && <BulkConfirmButton />
        }
        title={isShowTitle ? "Subcontractor Adhoc Fees" : " "}
        pagination={
          <CustomPagination
            limit={
              isFixedHeight ? (
                <EmptyComponent useFixedHeight />
              ) : (
                <PaginationLimit />
              )
            }
          />
        }
      >
        <Datagrid expand={<SubcontractorAdjustmentPricePanel />}>
          <TextField source="id" emptyText="-" />
          <ReferenceField
            source="subcontractorId"
            label="Subcontractor Identifier"
            reference="subcontractors"
            link={false}
          >
            <TextField source="subcontractorIdentifier" emptyText="-" />
          </ReferenceField>
          <ReferenceField
            source="subcontractorId"
            label="Subcontractor MBC Code"
            reference="subcontractors"
            link={false}
          >
            <TextField source="mbcVendorId" emptyText="-" />
          </ReferenceField>
          <ReferenceField
            source="subcontractorId"
            reference="subcontractors"
            link={false}
          >
            <TextField source="name" emptyText="-" />
          </ReferenceField>
          <ReferenceField source="schoolId" label="School" reference="schools">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField
            label="Wave Name"
            source="busSchedulePlanId"
            reference="busSchedulePlans"
            link={false}
          >
            <ReferenceField
              source="busScheduleId"
              reference="busSchedules"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
          </ReferenceField>
          <TextField label="Route Name" source="routeName" emptyText="-" />
          <CustomPriceField source="price" />
          <StatusField source="status" />
          <FunctionField
            label="Updated By"
            render={(record: any) => (
              <>
                {record.updatedBy === null ? (
                  "-"
                ) : (
                  <UpdatedByField data={record.updatedBy} />
                )}
              </>
            )}
          />

          <FunctionField
            label="Actions"
            render={(record: any) => (
              <ActionsMenu
                numberOfActions={5}
                render={[
                  isPending && availableOnlySuperAdmin() && (
                    <ApproveButton id={record.id} />
                  ),
                  isPending && availableOnlySuperAdmin() && (
                    <RejectButton id={record.id} />
                  ),
                  <SubcontractorAdjustmentPriceEditButton {...record} />,
                  availableOnlySuperAdmin() && <DeleteButton id={record.id} />,
                  <LogButton
                    subcontractorAdjustmentPriceId={record.id}
                    subcontractorId={record.subcontractorId}
                    routeId={record.routeId}
                    filterStatus={
                      record.status === "approved" ? "Archived" : "Approved"
                    }
                    currentStatus={
                      record.status === "draft" ? "unapproved" : record.status
                    }
                  />,
                ]}
              />
            )}
          />
        </Datagrid>
      </PaddingDenseStyled2List>
    </ResourceContextProvider>
  );
};

export default SubcontractorAdjustmentPriceTable;
